import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { useAuth, API, BACKEND_URL } from "@/App";
import axios from "axios";
import usePipWindow, { isPipSupported } from "@/components/pip/usePipWindow";
import PipOverlay from "@/components/pip/PipOverlay";
import { PictureInPicture2, AlertCircle, Loader2 } from "lucide-react";

export default function PipLauncher() {
  const { eventId } = useParams();
  const { user } = useAuth();

  const [event, setEvent] = useState(null);
  const [items, setItems] = useState([]);
  const [cards, setCards] = useState([]);
  const [coverage, setCoverage] = useState({});
  const [lastTranscript, setLastTranscript] = useState(null);
  const [livePartial, setLivePartial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sseConnected, setSseConnected] = useState(false);

  const sseRef = useRef(null);
  const { isPipOpen, portalRoot, openPip, closePip } = usePipWindow();

  const fetchMeetingData = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const [eventResp, agendaResp, cardsResp, coverageResp] = await Promise.all([
        axios.get(`${API}/events/${eventId}`, { withCredentials: true }),
        axios.get(`${API}/agendas/${eventId}`, { withCredentials: true }),
        axios.get(`${API}/meetings/${eventId}/cards`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/meetings/${eventId}/coverage`, { withCredentials: true }).catch(() => ({ data: { items: [] } })),
      ]);

      setEvent(eventResp.data);
      if (agendaResp.data?.items) {
        setItems(agendaResp.data.items);
      }
      if (Array.isArray(cardsResp.data)) {
        setCards(cardsResp.data.map((c) => ({ ...c, pinned: c.pinned ?? false, used: c.used ?? false })));
      }
      if (coverageResp.data?.items) {
        const map = {};
        coverageResp.data.items.forEach((i) => { map[i.item_id] = i; });
        setCoverage(map);
      }
    } catch (e) {
      setError("Failed to load meeting data.");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Initial data fetch
  useEffect(() => { fetchMeetingData(); }, [fetchMeetingData]);

  // SSE connection
  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    let retryDelay = 1000;
    let retryTimer = null;
    const MAX_RETRY_DELAY = 30000;

    const connect = () => {
      if (cancelled) return;
      const source = new EventSource(
        `${BACKEND_URL}/api/meetings/${eventId}/stream`,
        { withCredentials: true }
      );

      source.onopen = () => {
        setSseConnected(true);
        retryDelay = 1000;
      };

      source.addEventListener("card", (evt) => {
        try {
          const card = JSON.parse(evt.data);
          setCards((prev) => {
            if (prev.some((c) => c.card_id === card.card_id)) return prev;
            return [{ ...card, pinned: false, used: false }, ...prev];
          });
        } catch (e) {
          console.warn("SSE card parse error:", e);
        }
      });

      source.addEventListener("transcript", (evt) => {
        try {
          const seg = JSON.parse(evt.data);
          if (seg.is_final) {
            setLastTranscript({ speaker: seg.speaker, text: seg.text });
            setLivePartial(null);
          } else {
            setLivePartial(seg);
          }
        } catch (e) {
          console.warn("SSE transcript parse error:", e);
        }
      });

      source.addEventListener("agenda_coverage", (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (user?.user_id && data.target_user_id !== user.user_id) return;
          const items = data.items || [];
          setCoverage((prev) => {
            const next = { ...prev };
            items.forEach((i) => {
              const existing = next[i.item_id];
              const covered = (i.covered ?? false) || (existing?.covered ?? false);
              const subPoints = (i.sub_points || []).map((sp, idx) => {
                const byText = sp.text && existing?.sub_points?.find((e) => e.text === sp.text);
                const byIdx = existing?.sub_points?.[idx];
                const prev = byText || byIdx;
                return { ...sp, covered: (sp.covered ?? false) || (prev?.covered ?? false) };
              });
              let notes = [...(i.notes || [])];
              if (existing?.notes?.length) {
                const seen = new Set(notes.map((n) => (n || "").toLowerCase()));
                existing.notes.forEach((n) => {
                  if (n && !seen.has((n || "").toLowerCase())) {
                    notes.push(n);
                    seen.add((n || "").toLowerCase());
                  }
                });
              }
              notes = notes.slice(0, 5);
              next[i.item_id] = { ...i, covered, sub_points: subPoints, notes };
            });
            return next;
          });
        } catch (e) {
          console.warn("SSE agenda_coverage parse error:", e);
        }
      });

      source.onerror = () => {
        setSseConnected(false);
        source.close();
        if (!cancelled) {
          retryTimer = setTimeout(connect, retryDelay);
          retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY);
        }
      };

      sseRef.current = source;
    };

    connect();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      sseRef.current?.close();
      sseRef.current = null;
    };
  }, [eventId, user?.user_id]);

  // Auto-clear stale transcript after 20s of silence
  useEffect(() => {
    if (!lastTranscript) return;
    const timer = setTimeout(() => setLastTranscript(null), 20000);
    return () => clearTimeout(timer);
  }, [lastTranscript]);

  // Update document title
  useEffect(() => {
    document.title = event?.title
      ? `Overlay — ${event.title}`
      : "Overlay";
    return () => { document.title = "Shadow"; };
  }, [event?.title]);

  const handleOpenPip = useCallback(async () => {
    try {
      setError(null);
      await openPip({ width: 380, height: 540 });
    } catch (e) {
      setError(e.message);
    }
  }, [openPip]);

  const myUserId = user?.user_id;
  const displayedCards = cards
    .filter((c) => c.target_user_id == null || c.target_user_id === myUserId)
    .sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return (b.timestamp || "").localeCompare(a.timestamp || "");
    });

  if (!isPipSupported()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(40,14%,96%)] p-6">
        <div className="bg-white rounded-2xl border border-border p-8 max-w-sm text-center shadow-sm">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-foreground/80 mb-2">Browser Not Supported</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Overlay requires Chrome 116 or later. Please open this page in a supported browser.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(40,14%,96%)] p-6">
      <div className="bg-white rounded-2xl border border-border p-8 max-w-sm text-center shadow-sm">
        {loading ? (
          <>
            <Loader2 className="w-8 h-8 text-muted-foreground animate-spin mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Loading meeting...</p>
          </>
        ) : error ? (
          <>
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
            <p className="text-sm text-red-600 mb-3">{error}</p>
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={fetchMeetingData}
                className="text-sm font-medium text-primary hover:text-primary/80 underline"
              >
                Retry
              </button>
              <button
                onClick={() => window.close()}
                className="text-sm text-muted-foreground hover:text-foreground underline"
              >
                Close tab
              </button>
            </div>
          </>
        ) : isPipOpen ? (
          <>
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <PictureInPicture2 className="w-6 h-6 text-emerald-600" />
            </div>
            <h2 className="text-lg font-semibold text-foreground/80 mb-1">Overlay Active</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-1">
              {event?.title || "Meeting"}
            </p>
            <div className="flex items-center justify-center gap-1.5 mb-5">
              <span className={`w-1.5 h-1.5 rounded-full ${sseConnected ? "bg-emerald-500 animate-pulse" : "bg-amber-400"}`} />
              <span className="text-[11px] text-muted-foreground/50">
                {sseConnected ? "Live" : "Connecting..."}
              </span>
            </div>
            <p className="text-xs text-muted-foreground/40 leading-relaxed">
              Keep this tab open in the background. Switch to your meeting tab — the overlay floats on top.
            </p>
            <button
              onClick={closePip}
              className="mt-5 text-sm text-muted-foreground hover:text-foreground underline"
            >
              Close overlay
            </button>
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-2xl bg-primary/5 flex items-center justify-center mx-auto mb-4">
              <PictureInPicture2 className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-lg font-semibold text-foreground/80 mb-1">Overlay</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-1">
              {event?.title || "Meeting"}
            </p>
            <div className="flex items-center justify-center gap-1.5 mb-5">
              <span className={`w-1.5 h-1.5 rounded-full ${sseConnected ? "bg-emerald-500 animate-pulse" : "bg-amber-400"}`} />
              <span className="text-[11px] text-muted-foreground/50">
                {sseConnected ? "Connected" : "Connecting..."}
              </span>
            </div>
            <button
              onClick={handleOpenPip}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium shadow-sm hover:opacity-90 transition-opacity"
            >
              <PictureInPicture2 className="w-4 h-4" />
              Launch Overlay
            </button>
            <p className="text-xs text-muted-foreground/40 mt-4 leading-relaxed">
              A floating window will appear on top of your meeting. Keep this tab open in the background.
            </p>
          </>
        )}
      </div>

      {/* Portal into PiP window */}
      {isPipOpen && portalRoot &&
        createPortal(
          <PipOverlay
            items={items}
            coverage={coverage}
            cards={displayedCards}
            meetingTitle={event?.title}
            transcriptSpeaker={livePartial?.speaker || lastTranscript?.speaker}
            transcriptLine={livePartial?.text || lastTranscript?.text}
          />,
          portalRoot
        )
      }
    </div>
  );
}
